// Phase 3.4 — Sales Return add/edit form.
//
// Replaces the antd <Drawer> + <Form> (AutoComplete / Select / MUI RadioGroup) in
// salesReturn.tsx with a shadcn <Sheet> + FieldRow layout. Presentation only: the
// form model stays the parent's `salesReturnFormData`; product search/select,
// invoice select, and submit all call the parent's existing handlers (same
// dispatched payload). The antd Form `rules` — required fields plus the
// quantity(<=available) and amount(<=qty*mrp) validators — are ported one-to-one
// into a local `validate()` gate. The antd AutoComplete becomes an Input + a
// results list (no Combobox primitive yet); Category/Sub-Category remain
// disabled/read-only exactly as in the legacy.
import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/sheet";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { FieldRow } from "../../shared/form-section";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import AppConstant from "../../Globals/AppConstants";

// Numeric-only keydown guard (legacy handleKeyPress).
const numericGuard = (e: any) => {
  const allowed = [8, 46, 37, 39];
  if (e.ctrlKey && (e.key === "c" || e.key === "v" || e.key === "a")) return;
  if (allowed.includes(e.keyCode)) return;
  if (/^[0-9]$/.test(e.key)) return;
  e.preventDefault();
};

// Mirrors the actual actions the bill-first "+Add" flow captures (see
// RETURN_TYPES in SalesReturnBillFormSheet.tsx) instead of the raw
// statusRefId names. Cancel Return is no longer offered — a pre-existing
// cancelled record just won't have either option pre-selected here, but can
// still be edited into Normal/Damage.
const STATUS_OPTIONS = [
  { value: "2", label: "Normal Return" },
  { value: "5", label: "Damage Return" },
];

export interface SalesReturnFormSheetProps {
  open: boolean;
  onClose: () => void;
  formData: any;
  searchOptions: any[];
  invoiceId: any[];
  categoryList: any[];
  subCategoryList: any[];
  quantity: any;
  saving: boolean;
  onProductSearch: (value: any) => void;
  onProductChange: (value: any) => void;
  onProductSelect: (value: any) => void;
  onInvoiceChange: (value: any) => void;
  onQtyChange: (value: any) => void;
  onChange: (patch: any) => void;
  onStatusChange: (statusRefId: any) => void;
  onSubmit: () => void;
}

type Errors = {
  product?: string; invoice?: string; category?: string; subCategory?: string; qty?: string; amount?: string;
};

export function SalesReturnFormSheet({
  open,
  onClose,
  formData,
  searchOptions,
  invoiceId,
  categoryList,
  subCategoryList,
  quantity,
  saving,
  onProductSearch,
  onProductChange,
  onProductSelect,
  onInvoiceChange,
  onQtyChange,
  onChange,
  onStatusChange,
  onSubmit,
}: SalesReturnFormSheetProps) {
  const [errors, setErrors] = React.useState<Errors>({});

  const showQty = formData?.isMultipleRecords == 0 && formData?.qty != null;

  // `quantity` is the returnable quantity for this bill item (quantityLeft, or
  // the billed quantity when nothing is left) resolved by the parent from the
  // bill line. The legacy check was `formData.qty > quantity`, which passed
  // silently whenever that lookup produced undefined (`5 > undefined` is
  // false) — so an unresolvable cap is now an error instead of a free pass.
  const maxQty = Number(quantity);
  const hasMaxQty = Number.isFinite(maxQty) && maxQty > 0;

  // Returns undefined when `value` is an acceptable return quantity.
  const validateQty = (value: any): string | undefined => {
    if (value === "" || value == null) return "Quantity is required!";
    const qty = Number(value);
    if (!Number.isFinite(qty) || !Number.isInteger(qty) || qty < 1) {
      return "Quantity must be a whole number of at least 1";
    }
    if (!hasMaxQty) return "Returnable quantity for this bill item could not be determined.";
    if (qty > maxQty) return `Return quantity cannot exceed the quantity bought — max ${maxQty}`;
    return undefined;
  };

  // Ported one-to-one from the antd Form.Item rules + validators.
  const validate = (): boolean => {
    const next: Errors = {};
    if (!formData?.productId) next.product = "Product id is required!";
    if (!formData?.invoiceId) next.invoice = "Invoice id is required!";
    if (!formData?.categoryId) next.category = "Category is required!";
    if (!formData?.subCategoryId) next.subCategory = "Sub-Category is required!";
    if (showQty) next.qty = validateQty(formData?.qty);
    if (!next.qty) delete next.qty;
    const amtLimit = formData?.qty * formData?.mrp;
    if (!formData?.returnedAmount && formData?.returnedAmount !== 0) next.amount = "Returned Amount is required!";
    else if (Number(formData?.returnedAmount) > amtLimit) next.amount = `Entered Amount must be less than or equal to ₹ ${amtLimit}`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit();
  };

  const catName = (categoryList || []).find((c: any) => c?.categoryId == formData?.categoryId)?.categoryName;
  const subName = (subCategoryList || []).find((s: any) => s?.subCategoryId == formData?.subCategoryId)?.subCategoryName;
  const invoiceLabel = (invoiceId || []).find((o: any) => String(o?.invoiceId) === String(formData?.invoiceId))?.invoice
    ?? formData?.invoiceId ?? "";

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) { setErrors({}); onClose(); } }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>Sales Return</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          {/* Product Id / Invoice Id — this sheet is edit-only now (creating a
              new return goes through the bill-first "+Add" flow), so both
              identify a fixed, already-created record and stay disabled;
              only Status/Returned Amount (and Qty, for bulk items) can
              change. */}
          <FieldRow label="Product Id" required error={errors.product}>
            <Input value={formData?.productId ?? ""} disabled />
          </FieldRow>

          <FieldRow label="Invoice Id" required error={errors.invoice}>
            <Input value={invoiceLabel} disabled />
          </FieldRow>

          {/* Category / Sub-Category are disabled (populated by the product select). */}
          <FieldRow label="Category" required error={errors.category}>
            <Input value={catName ?? ""} disabled readOnly />
          </FieldRow>
          <FieldRow label="SubCategory" required error={errors.subCategory}>
            <Input value={subName ?? ""} disabled readOnly />
          </FieldRow>

          {showQty && (
            <FieldRow
              label="Qty"
              required
              error={errors.qty}
              hint={hasMaxQty ? `Max ${maxQty} returnable` : undefined}
            >
              <Input
                value={formData?.qty ?? ""}
                onKeyDown={numericGuard}
                onChange={(e) => {
                  onQtyChange(e.target.value);
                  // Re-check as they type rather than just clearing the error,
                  // so the cap surfaces before Save is pressed.
                  setErrors((er) => ({ ...er, qty: validateQty(e.target.value) }));
                }}
              />
            </FieldRow>
          )}

          <FieldRow label="Status" required>
            <RadioGroup
              className="flex flex-wrap gap-4"
              value={formData?.statusRefId != null ? String(formData.statusRefId) : undefined}
              onValueChange={(v) => onStatusChange(Number(v))}
            >
              {STATUS_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-1.5 text-sm">
                  <RadioGroupItem value={o.value} /> {o.label}
                </label>
              ))}
            </RadioGroup>
          </FieldRow>

          <FieldRow label="Returned Amount" required error={errors.amount}>
            <Input
              value={formData?.returnedAmount ?? ""}
              onKeyDown={numericGuard}
              onChange={(e) => { onChange({ returnedAmount: e.target.value }); setErrors((er) => ({ ...er, amount: undefined })); }}
            />
          </FieldRow>

          <div className="mt-auto flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { setErrors({}); onClose(); }}>{AppConstant?.cancel}</Button>
            <Button type="submit" disabled={saving}>{AppConstant?.save}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default SalesReturnFormSheet;
